import { PRIORITIES, VISIBILITIES } from '../constants';
import { LANE_ATTRIBUTES, type LaneAttribute } from '../lane-attributes';
import { m, type Message } from '@/i18n';
import { errOf, isRecord, ok, type Command, type DomainError, type Result } from '../types';
import type { ActTarget, PendingStimulus } from './types';

/**
 * **A stimulus off the wire** (docs/RESOLUTION.md §1 step 1; docs/SYNC.md
 * §7.4). A gesture the device queued comes back as JSON the server did not
 * write: the offline queue stores the `PendingStimulus` itself in IndexedDB
 * and hands it to the replay endpoint, and nothing on that path is typed.
 * This file is the one rule for what a valid one is, so the replay refuses a
 * malformed entry with a typed error BEFORE the resolver runs, and never hands
 * the pipeline a shape it did not build.
 *
 * Decoding REBUILDS the value from the fields it knows: an unknown key is
 * dropped, a wrong type refuses. Legality is not decided here - an empty title
 * or a tag that is not a path is `decide`'s refusal, as it is live.
 *
 * The live path takes the same rule: `actOnItem` and the board's lane drop
 * (src/app/actions.ts) decode the client's gesture before `stimulusOf`.
 *
 * Pure, and free of Node: the item page asks the same question of a gesture
 * before it defers one (`queueable`, src/app/items/[itemId]/deferred.ts).
 */

/** What the device's own dry run expected the item's atoms to become. */
export interface ExpectedAtoms {
  delivered?: boolean;
  closed?: boolean;
  active?: boolean;
}

/** A queued gesture as the replay reads it: the stimulus with its payload,
 *  the surface it was taken on, and the device's expectation. */
export interface DecodedGesture {
  stimulus: PendingStimulus;
  surface: string;
  expected?: ExpectedAtoms;
}

const invalid = (message: Message): { ok: false; error: DomainError } =>
  errOf('invalid_input', message);

/**
 * The longest string any field of a queued stimulus may carry. Generous on
 * purpose: a title's or a tag's legality stays `decide`'s; this only bounds the
 * size of what reaches it, so a device cannot hand the pipeline megabytes.
 */
export const STIMULUS_TEXT_MAX = 8192;

/** A string within the bound. */
const text = (v: unknown): v is string => typeof v === 'string' && v.length <= STIMULUS_TEXT_MAX;

type Field = (v: unknown) => boolean;
const str: Field = text;
const bool: Field = (v) => typeof v === 'boolean';
const nullableStr: Field = (v) => v === null || text(v);
const priority: Field = (v) => v === null || (PRIORITIES as readonly unknown[]).includes(v);
const visibility: Field = (v) => (VISIBILITIES as readonly unknown[]).includes(v);
const seatRole: Field = (v) => v === 'contributor' || v === 'requester';
const refs: Field = (v) =>
  Array.isArray(v) && v.length <= STIMULUS_TEXT_MAX && v.every((r) => text(r));

/** Each command's fields: the validator, and whether the field may be absent.
 *  Keyed by every `Command['type']`, so a new command that is not listed here
 *  fails the typecheck rather than the replay. */
const COMMAND_FIELDS: Record<Command['type'], Record<string, [Field, 'required' | 'optional']>> = {
  Tender: {},
  Retract: {},
  SetActive: { active: [bool, 'required'] },
  Close: { reason: [str, 'optional'] },
  Reopen: {},
  Claim: {},
  Accept: { note: [str, 'optional'] },
  Dispute: { reason: [str, 'optional'] },
  Resolve: {},
  SetPriority: { priority: [priority, 'required'] },
  SetAcceptanceCriteria: { text: [nullableStr, 'required'] },
  SetDescription: { text: [nullableStr, 'required'] },
  SetNote: { text: [nullableStr, 'required'] },
  SetTitle: { text: [str, 'required'] },
  SetVisibility: { visibility: [visibility, 'required'] },
  SetInheritsAccess: { inheritsAccess: [bool, 'required'] },
  SetReviewRequired: { required: [bool, 'required'] },
  Reassign: { role: [seatRole, 'required'], to: [refs, 'required'] },
  Comment: { text: [str, 'required'] },
  Acknowledge: {},
  Tag: { tag: [str, 'required'] },
  Untag: { tag: [str, 'required'] },
};

/** A command off the wire, rebuilt from the fields its type declares. Pure. */
export function decodeCommand(value: unknown): Result<Command> {
  if (!isRecord(value) || !text(value.type)) {
    return invalid(m('itemErrors.wire.noCommand'));
  }
  const type = value.type;
  if (!Object.hasOwn(COMMAND_FIELDS, type)) {
    return invalid(m('itemErrors.wire.notACommand', { type }));
  }
  const fields = COMMAND_FIELDS[type as Command['type']];
  const out: Record<string, unknown> = { type };
  for (const [name, [valid, presence]] of Object.entries(fields)) {
    const field = value[name];
    if (field === undefined) {
      if (presence === 'required')
        return invalid(m('itemErrors.wire.commandMissingField', { type, name }));
      continue;
    }
    if (!valid(field)) return invalid(m('itemErrors.wire.commandMalformedField', { type, name }));
    out[name] = Array.isArray(field) ? [...(field as unknown[])] : field;
  }
  return ok(out as Command);
}

function decodeTarget(value: unknown): Result<ActTarget> {
  if (!isRecord(value)) return invalid(m('itemErrors.wire.noTarget'));
  if ('command' in value) {
    const command = decodeCommand(value.command);
    return command.ok ? ok({ command: command.value }) : command;
  }
  if ((LANE_ATTRIBUTES as readonly unknown[]).includes(value.attribute) && text(value.value)) {
    return ok({ attribute: value.attribute as LaneAttribute, value: value.value });
  }
  return invalid(m('itemErrors.wire.badTarget'));
}

/**
 * A `PendingStimulus` off the wire, rebuilt from its kind's own payload. A
 * tick or an un-tick carries none (an un-tick may name the origin it takes
 * back), which is also what makes an entry queued before the payload was
 * stored - `{ kind: 'tick' }` and nothing else - decode as itself. Pure.
 */
export function decodePendingStimulus(value: unknown): Result<PendingStimulus> {
  if (!isRecord(value)) return invalid(m('itemErrors.wire.notAGesture'));
  const optional = (name: string): Result<string | undefined> =>
    value[name] === undefined || text(value[name])
      ? ok(value[name] as string | undefined)
      : invalid(m('itemErrors.wire.gestureMalformedField', { kind: String(value.kind), name }));
  const required = (name: string): Result<string> =>
    text(value[name])
      ? ok(value[name])
      : invalid(m('itemErrors.wire.gestureMissingField', { kind: String(value.kind), name }));

  switch (value.kind) {
    case 'tick':
      return ok({ kind: 'tick' });
    case 'untick': {
      const originKey = optional('originKey');
      if (!originKey.ok) return originKey;
      return ok(
        originKey.value === undefined
          ? { kind: 'untick' }
          : { kind: 'untick', originKey: originKey.value },
      );
    }
    case 'act': {
      const target = decodeTarget(value.target);
      return target.ok ? ok({ kind: 'act', target: target.value }) : target;
    }
    case 'cancel': {
      const reason = optional('reason');
      if (!reason.ok) return reason;
      return ok(
        reason.value === undefined ? { kind: 'cancel' } : { kind: 'cancel', reason: reason.value },
      );
    }
    case 'edge': {
      const edge = value.edge;
      if (
        edge !== 'done' &&
        edge !== 'ready' &&
        edge !== 'accept' &&
        edge !== 'deny' &&
        edge !== 'reopen'
      ) {
        return invalid(m('itemErrors.wire.gestureMalformedField', { kind: 'edge', name: 'edge' }));
      }
      const note = optional('note');
      if (!note.ok) return note;
      const reason = optional('reason');
      if (!reason.ok) return reason;
      return ok({
        kind: 'edge',
        edge,
        ...(note.value === undefined ? {} : { note: note.value }),
        ...(reason.value === undefined ? {} : { reason: reason.value }),
      });
    }
    case 'move': {
      const flowId = required('flowId');
      if (!flowId.ok) return flowId;
      const toStageKey = required('toStageKey');
      if (!toStageKey.ok) return toStageKey;
      return ok({ kind: 'move', flowId: flowId.value, toStageKey: toStageKey.value });
    }
    case 'retitle': {
      const text = required('text');
      return text.ok ? ok({ kind: 'retitle', text: text.value }) : text;
    }
    case 'retag': {
      const from = required('from');
      if (!from.ok) return from;
      const to = required('to');
      if (!to.ok) return to;
      return ok({ kind: 'retag', from: from.value, to: to.value });
    }
    default:
      return invalid(m('itemErrors.wire.unknownKind'));
  }
}

/**
 * A queued gesture off the wire: its stimulus (`decodePendingStimulus`, over
 * the same object, since the stored entry carries the payload beside its
 * envelope), the surface it was taken on, and the expected atoms, each atom a
 * boolean or absent. Pure.
 */
export function decodeQueuedGesture(value: unknown): Result<DecodedGesture> {
  const stimulus = decodePendingStimulus(value);
  if (!stimulus.ok) return stimulus;
  const envelope = value as Record<string, unknown>;
  if (!text(envelope.surface)) {
    return invalid(m('itemErrors.wire.noSurface'));
  }
  if (envelope.expected === undefined) {
    return ok({ stimulus: stimulus.value, surface: envelope.surface });
  }
  if (!isRecord(envelope.expected)) return invalid(m('itemErrors.wire.badExpectation'));
  const expected: ExpectedAtoms = {};
  for (const atom of ['delivered', 'closed', 'active'] as const) {
    const reading = envelope.expected[atom];
    if (reading === undefined) continue;
    if (typeof reading !== 'boolean') {
      return invalid(m('itemErrors.wire.badExpectation'));
    }
    expected[atom] = reading;
  }
  return ok({ stimulus: stimulus.value, surface: envelope.surface, expected });
}
